import { HELP_TAB_GROUPS, tabGroupOf } from './helpTabGroups';

/**
 * Map the live pathname to the catalog route its help is filed under (ADR-0066).
 *
 * <p>Parameterized screens (e.g. `/items/master/:ownerId/:itemId` after saving a New Item, John's
 * pilot in docs/help_project/requests.md) show the help of their catalog route. Exact match
 * first, else the LONGEST known route that is a path-SEGMENT prefix of the pathname
 * (`/items/master/ACC/X` → `/items/master`; `/items` never matches `/items/master`, and
 * `/items/mast` never matches `/items/master`), else null.
 *
 * <p>Sibling tabs (I-004, decision Q163): a pathname with no help of its own that is a tab of a
 * {@link HELP_TAB_GROUPS} group shows the help of the FIRST tab in that group that has a help
 * file (normally the group's first tab). The panel then scrolls to that file's
 * `{tab=<pathname>}` section, if any (`tabSectionAnchor`).
 */
export function resolveHelpRoute(
  pathname: string,
  knownRoutes: Iterable<string>,
  tabGroups: readonly (readonly string[])[] = HELP_TAB_GROUPS,
): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const known = [...knownRoutes];
  if (!known.includes(path)) {
    const host = tabGroupOf(path, tabGroups)?.find((r) => known.includes(r));
    if (host !== undefined) return host;
  }
  let best: string | null = null;
  for (const raw of known) {
    const route = raw.length > 1 ? raw.replace(/\/+$/, '') : raw;
    if (route === path) return raw;
    // `/` only ever matches exactly; it is no one's parent screen.
    if (route === '/') continue;
    if (path.startsWith(`${route}/`) && (best == null || route.length > best.replace(/\/+$/, '').length)) {
      best = raw;
    }
  }
  return best;
}
